import React, { useState, useRef, useEffect, useMemo, useImperativeHandle, forwardRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import api from '../services/api';
import toast from '../utils/toast';
import { getAvailableFields } from '../utils/standardFields';

// Set up pdfjs worker - use local file from public folder
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.js`;

// Suppress font warnings from pdfjs (harmless but annoying)
if (typeof window !== 'undefined') {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('TT: undefined function')) {
      return; // Suppress font warnings
    }
    originalWarn.apply(console, args);
  };
}

const TemplateBuilder = forwardRef(({ template, onSave, onCancel }, ref) => {
  
  // Generate template code from name (same logic as backend)
  const generateTemplateCode = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  };
  
  const templateType = template?.templateType || 'invoice';
  
  // Get all available fields
  const ALL_AVAILABLE_FIELDS = useMemo(() => {
    const available = getAvailableFields(templateType);
    // Sort: documentType first, then crucial fields, then mandatory fields, then optional fields
    return available.sort((a, b) => {
      if (a.standardName === 'documentType') return -1;
      if (b.standardName === 'documentType') return 1;
      if (a.isCrucial && !b.isCrucial) return -1;
      if (!a.isCrucial && b.isCrucial) return 1;
      if (a.isMandatory && !b.isMandatory) return -1;
      if (!a.isMandatory && b.isMandatory) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [templateType]);
  
  // Helper to check coordinates for a field
  const getFieldCoordinates = useCallback((standardName, templateCode) => {
    if (!template?.coordinates) return { hasCoordinates: false, coordinates: null };
    
    const prefixedId = templateCode ? `${templateCode}_${standardName}` : standardName;
    const oldSnakeCase = standardName.replace(/([A-Z])/g, '_$1').toLowerCase();
    const prefixedOld = templateCode ? `${templateCode}_${oldSnakeCase}` : oldSnakeCase;
    
    const coords = template.coordinates[prefixedId] || 
                  template.coordinates[standardName] || 
                  template.coordinates[prefixedOld] ||
                  template.coordinates[oldSnakeCase];
    
    if (coords && (
      (coords.normalized && coords.normalized.left !== undefined) ||
      (coords.x !== undefined && coords.y !== undefined && coords.width && coords.height)
    )) {
      return { hasCoordinates: true, coordinates: coords };
    }
    return { hasCoordinates: false, coordinates: null };
  }, [template?.coordinates]);
  
  // Initialize all fields
  const initializeFields = useCallback(() => {
    const templateCode = template?.code || generateTemplateCode(template?.name || '');
    return ALL_AVAILABLE_FIELDS.map(field => {
      const { hasCoordinates, coordinates } = getFieldCoordinates(field.standardName, templateCode);
      return {
        standardName: field.standardName,
        label: field.displayName,
        mapsTo: field.standardName,
        required: field.isMandatory,
        isCrucial: field.isCrucial,
        hasCoordinates,
        coordinates
      };
    });
  }, [ALL_AVAILABLE_FIELDS, template?.code, template?.name, getFieldCoordinates]);
  
  const initialFields = initializeFields();
  
  const [templateData, setTemplateData] = useState({
    name: template?.name || '',
    templateType: templateType,
    isDefault: template?.isDefault || false,
    fields: initialFields
  });

  // Keep regions for rendering (synced with fields)
  const [regions, setRegions] = useState(() => {
    // Convert fields to regions format for existing rendering code
    const templateCode = template?.code || generateTemplateCode(template?.name || '');
    const regionsList = [];
    
    initialFields.forEach(field => {
      if (field.hasCoordinates && field.coordinates) {
        const fieldId = templateCode ? `${templateCode}_${field.standardName}` : field.standardName;
        regionsList.push({
          fieldId: fieldId,
          label: field.label,
          ...field.coordinates,
          extractedText: ''
        });
      }
    });
    
    return regionsList;
  });
  
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfPage, setPdfPage] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // Current page being viewed/edited
  const [totalPages, setTotalPages] = useState(1); // Total pages in PDF
  const [scale, setScale] = useState(1.0); // 100% zoom default
  const [drawingMode, setDrawingMode] = useState(false);
  const [pendingFieldLabel, setPendingFieldLabel] = useState(null); // Field label waiting for region drawing
  const [drawingBox, setDrawingBox] = useState(null);
  const [startPos, setStartPos] = useState(null);
  const [extractingRegion, setExtractingRegion] = useState(false);
  
  // Test Parse modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testFile, setTestFile] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null); // Track current render task to cancel if needed
  const handleTestParseRef = useRef(null); // Store handleTestParse function reference
  
  // Render PDF page on canvas
  const renderPDF = async (pdf, pageNum = 1) => {
    try {
      // Cancel any previous render task
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null;
      }
      
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      
      setPdfPage(page);
      
      // Get actual PDF page dimensions (in points, not viewport pixels)
      const pdfPageSize = page.view;
      const pdfPageWidth = pdfPageSize[2] - pdfPageSize[0]; // right - left
      const pdfPageHeight = pdfPageSize[3] - pdfPageSize[1]; // top - bottom
      
      setPdfDimensions({
        width: viewport.width, // Display width in pixels
        height: viewport.height, // Display height in pixels
        pdfWidth: pdfPageWidth, // PDF width in points
        pdfHeight: pdfPageHeight, // PDF height in points
        numPages: pdf.numPages
      });
      
      // Update total pages
      setTotalPages(pdf.numPages);
      
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        
        // Clear the canvas first to avoid rendering artifacts
        context.clearRect(0, 0, canvas.width, canvas.height);
        
        // Handle device pixel ratio for high-DPI displays
        const devicePixelRatio = window.devicePixelRatio || 1;
        const displayWidth = viewport.width;
        const displayHeight = viewport.height;
        
        // Set actual canvas size in memory (scaled by device pixel ratio)
        canvas.width = displayWidth * devicePixelRatio;
        canvas.height = displayHeight * devicePixelRatio;
        
        // Scale the canvas back down using CSS
        canvas.style.width = displayWidth + 'px';
        canvas.style.height = displayHeight + 'px';
        
        // Scale the drawing context so everything draws at the correct size
        context.scale(devicePixelRatio, devicePixelRatio);
        
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        
        // Start render and track the task
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        
        // Wait for render to complete
        await renderTask.promise;
        
        // Clear the reference once done
        renderTaskRef.current = null;
      }
    } catch (error) {
      // Ignore cancellation errors
      if (error.name === 'RenderingCancelledException' || error.message?.includes('cancelled')) {
        // Expected when zoom/page changes mid-render; silently ignore.
        return;
      }
      console.error('Error rendering PDF:', error);
      toast.error('Failed to render PDF: ' + error.message);
      renderTaskRef.current = null;
    }
  };
  
  // Navigate to a specific page
  const goToPage = async (pageNum) => {
    if (!pdfDoc || pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
    await renderPDF(pdfDoc, pageNum);
  };
  
  // Navigate to next page
  const nextPage = async () => {
    if (currentPage < totalPages) {
      await goToPage(currentPage + 1);
    }
  };
  
  // Navigate to previous page
  const prevPage = async () => {
    if (currentPage > 1) {
      await goToPage(currentPage - 1);
    }
  };
  
  // Load PDF from file or stored path
  const loadPDF = async (source) => {
    try {
      let pdfData;
      
      if (source instanceof File) {
        const arrayBuffer = await source.arrayBuffer();
        pdfData = { data: arrayBuffer };
      } else if (typeof source === 'string') {
        // Base64 data URL
        const base64Data = source.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        pdfData = { data: bytes };
      }
      
      const loadingTask = pdfjsLib.getDocument(pdfData);
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setCurrentPage(1); // Reset to page 1 when loading new PDF
      await renderPDF(pdf, 1);
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to load PDF: ' + error.message);
    }
  };
  
  // Load stored PDF when editing existing template
  useEffect(() => {
    if (template?.id && template?.samplePdfPath && !pdfFile && !pdfDoc) {
      api.get(`/api/templates/${template.id}/pdf`)
        .then(res => {
          const dataUrl = res.data.pdfData; // Base64 data URL from backend
          
          // Convert base64 data URL to File object so we can use it for Test Parse
          const base64Data = dataUrl.split(',')[1];
          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/pdf' });
          const fileName = template.name ? `${template.name.replace(/[^a-z0-9]/gi, '_')}.pdf` : 'template.pdf';
          const file = new File([blob], fileName, { type: 'application/pdf' });
          setPdfFile(file);
          
          // Also load it for display
          loadPDF(dataUrl);
        })
        .catch(err => {
          console.warn('Could not load stored PDF:', err.response?.data?.message || err.message);
        });
    }
  }, [template?.id, template?.samplePdfPath]);
  
  // Compute template code from current name (updates when name changes)
  // Use template code from template if available, otherwise generate from current name
  const templateCode = useMemo(() => {
    return template?.code || generateTemplateCode(templateData.name || template?.name || '');
  }, [template?.code, templateData.name, template?.name]);
  
  // Check if document_type exists and is configured (with valid coordinates)
  const hasDocumentType = templateData.fields.some(f => 
    f.standardName === 'documentType' && f.hasCoordinates
  );
  
  // Handle PDF file selection
  const handlePdfSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      loadPDF(file);
    } else {
      toast.error('Please select a PDF file');
    }
  };
  
  // Start drawing box
  const handleMouseDown = (e) => {
    if (!drawingMode || !containerRef.current || !canvasRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    // Get canvas position relative to viewport (CSS pixels)
    const canvasRect = canvasRef.current.getBoundingClientRect();
    
    // Calculate coordinates in CSS pixels (display size, not internal canvas size)
    const x = e.clientX - canvasRect.left;
    const y = e.clientY - canvasRect.top;
    
    // Clamp to canvas display bounds
    const clampedX = Math.max(0, Math.min(x, canvasRect.width));
    const clampedY = Math.max(0, Math.min(y, canvasRect.height));
    
    setStartPos({ x: clampedX, y: clampedY });
    setDrawingBox({ x: clampedX, y: clampedY, width: 0, height: 0 });
  };
  
  // Update drawing box
  const handleMouseMove = (e) => {
    if (!drawingMode || !startPos || !containerRef.current || !canvasRef.current) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const canvasRect = canvasRef.current.getBoundingClientRect();
    
    // Calculate coordinates in CSS pixels (display size)
    const x = e.clientX - canvasRect.left;
    const y = e.clientY - canvasRect.top;
    
    // Clamp to canvas display bounds
    const clampedX = Math.max(0, Math.min(x, canvasRect.width));
    const clampedY = Math.max(0, Math.min(y, canvasRect.height));
    
    const newBox = {
      x: Math.min(startPos.x, clampedX),
      y: Math.min(startPos.y, clampedY),
      width: Math.abs(clampedX - startPos.x),
      height: Math.abs(clampedY - startPos.y)
    };

    setDrawingBox(newBox);
  };
  
  // Finish drawing box and extract text from region
  const handleMouseUp = async (e) => {
    if (!drawingMode || !startPos || !drawingBox || !pdfPage) return;
    
    // Only process if we actually drew a box
    if (drawingBox.width > 5 && drawingBox.height > 5) {
      // Use pendingFieldLabel (set from table button)
      const fieldLabel = pendingFieldLabel;
      
      if (!fieldLabel || !fieldLabel.trim()) {
        toast.error('Please enter a field label first');
        setDrawingMode(false);
        setDrawingBox(null);
        setStartPos(null);
        setPendingFieldLabel(null);
        return;
      }
      
      setExtractingRegion(true);
      
      try {
        // ====================================================================
        // NORMALIZED COORDINATE CONVERSION (0-1 system)
        // ====================================================================
        // This makes coordinates immune to zoom, screen size, DPI, resolution
        // 
        // Step 1: Get canvas display size (CSS pixels)
        // Step 2: Convert screen pixels → normalized (0-1) coordinates
        // Step 3: Store normalized coordinates in database
        // Step 4: When extracting, convert PDF coordinates → normalized
        // Step 5: Compare normalized values → perfect match every time
        // ====================================================================
        
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const viewWidth = canvasRect.width;  // Display width in CSS pixels
        const viewHeight = canvasRect.height; // Display height in CSS pixels
        
        // Convert screen pixels to normalized coordinates (0-1)
        // left: x position as fraction of width
        // top: y position as fraction of height  
        // right: (x + width) as fraction of width
        // bottom: (y + height) as fraction of height
        const left = drawingBox.x / viewWidth;
        const top = drawingBox.y / viewHeight;
        const right = (drawingBox.x + drawingBox.width) / viewWidth;
        const bottom = (drawingBox.y + drawingBox.height) / viewHeight;
        
        // Clamp to 0-1 range
        const normalizedCoords = {
          left: Math.max(0, Math.min(1, left)),
          top: Math.max(0, Math.min(1, top)),
          right: Math.max(0, Math.min(1, right)),
          bottom: Math.max(0, Math.min(1, bottom))
        };

        // Legacy PDF-point coordinates kept on the saved field/region for
        // backwards compatibility with older renderers that read x/y/width/
        // height when `normalized` is missing. PDF uses bottom-left origin so
        // Y is flipped from the top-left normalized space.
        const pdfPageSize = pdfPage.view;
        const pdfPageWidth = pdfPageSize[2] - pdfPageSize[0];
        const pdfPageHeight = pdfPageSize[3] - pdfPageSize[1];
        const pdfX = normalizedCoords.left * pdfPageWidth;
        const pdfWidth_coord = (normalizedCoords.right - normalizedCoords.left) * pdfPageWidth;
        const pdfHeight_coord = (normalizedCoords.bottom - normalizedCoords.top) * pdfPageHeight;
        const pdfY = pdfPageHeight - (normalizedCoords.bottom * pdfPageHeight);

        // Extract text from this specific region
        const formData = new FormData();

        // If we have a file, use it; otherwise use template ID
        if (pdfFile) {
          formData.append('pdf', pdfFile);
        } else if (template?.id) {
          formData.append('templateId', template.id);
        } else {
          throw new Error('No PDF available. Please upload a PDF first.');
        }

        // Send ONLY normalized coordinates (0-1 system) - bulletproof approach!
        // The backend will convert text item coordinates to normalized and compare
        formData.append('left', normalizedCoords.left);
        formData.append('top', normalizedCoords.top);
        formData.append('right', normalizedCoords.right);
        formData.append('bottom', normalizedCoords.bottom);
        formData.append('page', currentPage);

        const response = await api.post('/api/templates/extract-region-text', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const extractedText = response.data.text || '';
        const itemCount = response.data.itemCount || 0;

        if (!extractedText.trim()) {
          toast.warning(`No text found in this region (checked ${itemCount} items). Try a different area.`);
        } else {
          toast.success(`Found ${itemCount} text item(s): "${extractedText.substring(0, 50)}${extractedText.length > 50 ? '...' : ''}"`);
        }
        
        // Find the field by label
        const field = templateData.fields.find(f => f.label === fieldLabel);
        
        if (!field) {
          toast.error(`Field "${fieldLabel}" not found`);
          return;
        }
        
        const standardName = field.standardName;
        const fieldId = templateCode ? `${templateCode}_${standardName}` : standardName;
        
        const coordinatesData = {
          x: pdfX,
          y: pdfY,
          width: pdfWidth_coord,
          height: pdfHeight_coord,
          page: currentPage,
          normalized: normalizedCoords,
          label: fieldLabel
        };
        
        // Update the field
        setTemplateData(prev => ({
          ...prev,
          fields: prev.fields.map(f => 
            f.standardName === standardName
              ? { ...f, hasCoordinates: true, coordinates: coordinatesData }
              : f
          )
        }));
        
        // Also update regions for rendering (keep in sync)
        setRegions(prev => {
          const existingIndex = prev.findIndex(r => r.fieldId === fieldId);
          const regionData = {
            fieldId,
            label: fieldLabel,
            x: pdfX,
            y: pdfY,
            width: pdfWidth_coord,
            height: pdfHeight_coord,
            page: currentPage,
            normalized: normalizedCoords,
            extractedText
          };
          
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = regionData;
            return updated;
          } else {
            return [...prev, regionData];
          }
        });
        
        // Reset form
        setPendingFieldLabel(null);
        toast.success(`Area defined for: ${fieldLabel}`);
      } catch (error) {
        console.error('Error extracting region text:', error);
        toast.error('Failed to extract text from region: ' + (error.response?.data?.message || error.message));
      } finally {
        setExtractingRegion(false);
        setDrawingMode(false);
        setDrawingBox(null);
        setStartPos(null);
        setPendingFieldLabel(null);
      }
    } else {
      setDrawingMode(false);
      setDrawingBox(null);
      setStartPos(null);
      setPendingFieldLabel(null);
    }
  };
  
  // Save template
  const handleSave = async () => {
    if (!templateData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    // Ensure at least one field has coordinates
    const fieldsWithCoordinates = templateData.fields.filter(f => f.hasCoordinates);
    if (fieldsWithCoordinates.length === 0) {
      toast.error('Please define at least one field area');
      return;
    }
    
    // Ensure document_type exists
    if (!hasDocumentType) {
      toast.error('Document Type area is required. Please define it first.');
      return;
    }
    
    // Get template code for field ID prefixing (use existing template code or generate from name)
    const saveTemplateCode = template?.code || generateTemplateCode(templateData.name);
    
    // Convert fields to coordinates format - use standard field names directly
    const coordinates = {};
    
    // Get PDF page dimensions for coordinate conversion
    const pdfPageSize = pdfPage?.view;
    const pdfPageWidth = pdfPageSize ? (pdfPageSize[2] - pdfPageSize[0]) : (pdfDimensions?.width || 612);
    const pdfPageHeight = pdfPageSize ? (pdfPageSize[3] - pdfPageSize[1]) : (pdfDimensions?.height || 792);
    
    // Process all fields
    templateData.fields.forEach(field => {
      // Only include fields with coordinates
      if (field.hasCoordinates && field.coordinates) {
        const coords = field.coordinates;
        
        // Use standard field name directly (no template prefix in storage)
        // Backend will handle mapping
        const fieldId = saveTemplateCode ? `${saveTemplateCode}_${field.standardName}` : field.standardName;
        
        // Calculate PDF coordinates from normalized if not already stored
        let pdfX, pdfY, pdfWidth, pdfHeight;
        
        if (coords.x !== undefined && coords.y !== undefined && coords.width && coords.height) {
          // Use existing PDF coordinates if available
          pdfX = coords.x;
          pdfY = coords.y;
          pdfWidth = coords.width;
          pdfHeight = coords.height;
        } else if (coords.normalized) {
          // Calculate PDF coordinates from normalized
          pdfX = coords.normalized.left * pdfPageWidth;
          pdfWidth = (coords.normalized.right - coords.normalized.left) * pdfPageWidth;
          pdfHeight = (coords.normalized.bottom - coords.normalized.top) * pdfPageHeight;
          // PDF uses bottom-left origin, so flip Y
          pdfY = pdfPageHeight - (coords.normalized.bottom * pdfPageHeight);
        } else {
          return; // Skip if no valid coordinates
        }
        
        coordinates[fieldId] = {
          x: pdfX,
          y: pdfY,
          width: pdfWidth,
          height: pdfHeight,
          page: coords.page || 1,
          // Store normalized coordinates for accurate display (required!)
          normalized: coords.normalized,
          // Store the label so we can display it in parsing results
          label: field.label
        };
      }
    });

    // Verify document_type exists
    const docTypeFieldId = saveTemplateCode ? `${saveTemplateCode}_documentType` : 'documentType';
    if (!coordinates[docTypeFieldId]) {
      toast.error('Document Type area is missing. Please define it first.');
      return;
    }
    
    // Generate template code from name (same as backend)
    const code = generateTemplateCode(templateData.name);
    
    if (!code) {
      toast.error('Template name is required');
      return;
    }
    
    const formData = new FormData();
    formData.append('name', templateData.name);
    formData.append('templateType', templateData.templateType);
    formData.append('fileType', 'pdf');
    formData.append('coordinates', JSON.stringify(coordinates));
    formData.append('isDefault', templateData.isDefault || false);
    
    if (pdfFile) {
      // POST route expects 'sampleExcel', PUT route expects 'samplePdf'
      if (template?.id) {
        formData.append('samplePdf', pdfFile);
      } else {
        formData.append('sampleExcel', pdfFile); // Backend will handle based on fileType='pdf'
      }
    }
    
    try {
      const baseEndpoint = '/api/templates';
      
      if (template?.id) {
        await api.put(`${baseEndpoint}/${template.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template updated successfully');
      } else {
        await api.post(baseEndpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template created successfully');
      }
      
      if (onSave) onSave();
    } catch (err) {
      console.error('Error saving template:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
      toast.error('Failed to save template: ' + errorMessage);
    }
  };
  
  // Test parse handler
  const handleTestParse = useCallback(async () => {
    // Auto-use pdfFile if available, otherwise use testFile
    const fileToTest = pdfFile || testFile;
    if (!fileToTest) {
      toast.error('Please upload a PDF file first');
      return;
    }
    
    // Check if we have any field coordinates defined
    const fieldsWithCoordinates = templateData.fields.filter(f => f.hasCoordinates);
    if (fieldsWithCoordinates.length === 0) {
      toast.error('Please define at least one field area before testing');
      return;
    }
    
    setTesting(true);
    setTestResults(null);
    
    try {
      // Build coordinates object from current template data (same as handleSave)
      const testTemplateCode = template?.code || generateTemplateCode(templateData.name) || 'test_template';
      const coordinates = {};
      
      // Get PDF page dimensions for coordinate conversion
      const pdfPageSize = pdfPage?.view;
      const pdfPageWidth = pdfPageSize ? (pdfPageSize[2] - pdfPageSize[0]) : (pdfDimensions?.width || 612);
      const pdfPageHeight = pdfPageSize ? (pdfPageSize[3] - pdfPageSize[1]) : (pdfDimensions?.height || 792);
      
      templateData.fields.forEach(field => {
        if (field.hasCoordinates && field.coordinates) {
          const coords = field.coordinates;
          const fieldId = `${testTemplateCode}_${field.standardName}`;
          
          let pdfX, pdfY, pdfWidth, pdfHeight;
          
          if (coords.x !== undefined && coords.y !== undefined && coords.width && coords.height) {
            pdfX = coords.x;
            pdfY = coords.y;
            pdfWidth = coords.width;
            pdfHeight = coords.height;
          } else if (coords.normalized) {
            pdfX = coords.normalized.left * pdfPageWidth;
            pdfWidth = (coords.normalized.right - coords.normalized.left) * pdfPageWidth;
            pdfHeight = (coords.normalized.bottom - coords.normalized.top) * pdfPageHeight;
            pdfY = pdfPageHeight - (coords.normalized.bottom * pdfPageHeight);
          } else {
            return;
          }
          
          coordinates[fieldId] = {
            x: pdfX,
            y: pdfY,
            width: pdfWidth,
            height: pdfHeight,
            page: coords.page || 1,
            normalized: coords.normalized,
            label: field.label
          };
        }
      });
      
      // Create form data with the test file and template configuration
      const formData = new FormData();
      formData.append('file', fileToTest);
      formData.append('parser', 'local');
      formData.append('template', JSON.stringify({
        code: testTemplateCode,
        name: templateData.name || 'Test Template',
        templateType: templateData.templateType,
        fileType: 'pdf',
        coordinates: coordinates
      }));
      
      const response = await api.post('/api/parsing/test-parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000 // 5 minutes
      });
      
      const results = response.data.results || response.data;
      setTestResults({
        success: response.data.success,
        message: response.data.message,
        confidenceScore: results.confidence || 0,
        processingMethod: results.processingMethod || 'local_coordinates',
        extractedFields: results.parsedData || results.extractedFields || {},
        fullText: results.fullText || ''
      });
      
      toast.success('Test parse completed successfully');
    } catch (error) {
      console.error('Error testing parse:', error);
      toast.error('Test parse failed: ' + (error.response?.data?.message || error.message));
      setTestResults({
        success: false,
        message: error.response?.data?.message || error.message,
        error: true
      });
    } finally {
      setTesting(false);
    }
  }, [pdfFile, testFile, templateData, template, pdfPage, pdfDimensions, generateTemplateCode]);
  
  // Store handleTestParse in ref so it can be called from useImperativeHandle
  useEffect(() => {
    handleTestParseRef.current = handleTestParse;
  }, [handleTestParse]);
  
  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleSave: () => handleSave(),
    handleTestParse: () => {
      if (!pdfFile) {
        toast.error('Please upload a PDF file first');
        return;
      }
      setTestFile(pdfFile);
      setShowTestModal(true);
      // Auto-start test parse after modal opens
      setTimeout(() => {
        if (handleTestParseRef.current) {
          handleTestParseRef.current();
        }
      }, 300);
    },
    canSave: () => templateData.name.trim() && templateData.fields.some(f => f.hasCoordinates),
    canTest: () => templateData.fields.some(f => f.hasCoordinates),
    getState: () => ({
      hasName: !!templateData.name.trim(),
      hasFields: templateData.fields.some(f => f.hasCoordinates),
      isEditing: !!template?.id
    })
  }), [pdfFile, templateData, template]);
  
  // Re-render when scale or currentPage changes
  useEffect(() => {
    if (pdfDoc) {
      renderPDF(pdfDoc, currentPage);
    }
  }, [scale, currentPage]);
  
  return (
    <div className="row" style={{ height: 'calc(100vh - 200px)' }}>
      {/* Field Mapper - Left Column (33%) */}
      <div className="col-md-4">
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Template Configuration</h3>
          </div>
          <div className="card-body">
            {/* Template Name */}
            <div className="mb-3">
              <label className="form-label">
                Template Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                value={templateData.name}
                onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Makita Invoice Template"
                required
                readOnly={!!template?.id}
                disabled={!!template?.id}
              />
              <small className="text-muted">
                Code: <code>{generateTemplateCode(templateData.name) || 'template_name'}</code>
              </small>
            </div>
            
            {/* Template Type - Read Only */}
            <div className="mb-3">
              <label className="form-label">Template Type</label>
              <input
                type="text"
                className="form-control"
                value={templateData.templateType === 'invoice' ? 'Invoice' : templateData.templateType === 'credit_note' ? 'Credit Note' : 'Statement'}
                readOnly
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </div>
            
            {/* Is Default Checkbox */}
            <div className="mb-3">
              <label className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={templateData.isDefault || false}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, isDefault: e.target.checked }))}
                />
                <span className="form-check-label">Set as default template for this type</span>
              </label>
            </div>
            
            <hr />
            
            <h4 className="card-title mb-3">Field Mapper</h4>
            
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Maps To</th>
                    <th>Define Area</th>
                  </tr>
                </thead>
                <tbody>
                  {templateData.fields.map((field) => {
                    const fieldHasCoordinates = field.hasCoordinates;
                    const isDefining = drawingMode && pendingFieldLabel === field.label;
                    
                    return (
                      <tr 
                        key={field.standardName} 
                        className={!fieldHasCoordinates && field.required ? 'table-warning' : ''}
                      >
                        <td>
                          <strong>{field.label}</strong>
                          {field.isCrucial && (
                            <span className="badge bg-danger-lt ms-2" title="Crucial field - must parse correctly">Crucial</span>
                          )}
                          {field.required && !field.isCrucial && (
                            <span className="badge bg-warning-lt ms-2" title="Required field">Required</span>
                          )}
                          {field.hasCoordinates && field.coordinates?.page && (
                            <span className="badge bg-info-lt ms-2" title={`Extracted from page ${field.coordinates.page}`}>
                              Page {field.coordinates.page}
                            </span>
                          )}
                        </td>
                        <td>
                          <small className="text-muted">{field.mapsTo}</small>
                        </td>
                        <td>
                          {fieldHasCoordinates ? (
                            <div className="d-flex gap-2 align-items-center">
                              <span className="badge bg-success-lt">Defined</span>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setPendingFieldLabel(field.label);
                                  setDrawingMode(true);
                                  // Navigate to the page where this field is defined (if it has coordinates)
                                  if (field.coordinates?.page && field.coordinates.page !== currentPage) {
                                    goToPage(field.coordinates.page);
                                  }
                                  // Remove existing coordinates to allow redraw
                                  setTemplateData(prev => ({
                                    ...prev,
                                    fields: prev.fields.map(f => 
                                      f.standardName === field.standardName 
                                        ? { ...f, hasCoordinates: false, coordinates: null }
                                        : f
                                    )
                                  }));
                                }}
                                disabled={drawingMode || extractingRegion}
                                title="Redefine area"
                              >
                                ↻
                              </button>
                            </div>
                          ) : (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                setPendingFieldLabel(field.label);
                                setDrawingMode(true);
                              }}
                              disabled={!pdfDoc || drawingMode || extractingRegion}
                            >
                              {isDefining ? 'Drawing...' : 'Define Area'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {drawingMode && pendingFieldLabel && (
              <div className="alert alert-info mt-3">
                <strong>Drawing mode active</strong>
                <p className="mb-0 mt-1">Field: <strong>{pendingFieldLabel}</strong></p>
                <p className="mb-0">Click and drag on the PDF to select a region</p>
                <button
                  className="btn btn-sm btn-secondary mt-2"
                  onClick={() => {
                    setDrawingMode(false);
                    setDrawingBox(null);
                    setStartPos(null);
                    setPendingFieldLabel(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            
          </div>
        </div>
      </div>
      
      {/* PDF Preview - Right Column (66%) */}
      <div className="col-8" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h3 className="card-title mb-0">PDF Preview</h3>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                {/* Page Navigation */}
                {totalPages > 1 && (
                  <>
                    <div className="d-flex gap-2 align-items-center">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        title="Previous page"
                      >
                        ← Prev
                      </button>
                      <span className="text-muted">
                        Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        title="Next page"
                      >
                        Next →
                      </button>
                      {totalPages > 5 && (
                        <select
                          className="form-select form-select-sm"
                          style={{ width: 'auto' }}
                          value={currentPage}
                          onChange={(e) => goToPage(parseInt(e.target.value))}
                        >
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                            <option key={page} value={page}>Page {page}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="text-warning small">
                      <strong>Multi-page:</strong> Select regions on the correct page
                    </div>
                  </>
                )}
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0">Zoom:</label>
                  <input
                    type="range"
                    className="form-range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={scale}
                    onChange={(e) => setScale(parseFloat(e.target.value))}
                    style={{ width: '100px' }}
                  />
                  <span className="text-muted">{Math.round(scale * 100)}%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body" style={{ flex: 1, overflow: 'auto', padding: 0 }}>
            {!pdfDoc ? (
              <div className="text-center p-5" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handlePdfSelect}
                  className="form-control"
                  style={{ maxWidth: '400px', margin: '0 auto' }}
                />
                <p className="text-muted mt-3">Upload a sample PDF to define field regions</p>
              </div>
            ) : (
              <div
                ref={containerRef}
                style={{ 
                  position: 'relative', 
                  border: '1px solid #ddd',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  padding: '20px',
                  minHeight: '100%',
                  overflow: 'auto'
                }}
              >
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <canvas
                    ref={canvasRef}
                    style={{
                      border: '1px solid #ccc',
                      cursor: drawingMode ? 'crosshair' : 'default',
                      pointerEvents: 'none', // Canvas doesn't need pointer events - overlay handles them
                      display: 'block'
                    }}
                  />
                  
                  {/* Drawing overlay - positioned relative to canvas wrapper */}
                  {drawingMode && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        cursor: 'crosshair',
                        zIndex: 15,
                        backgroundColor: 'transparent',
                        pointerEvents: 'auto'
                      }}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                    >
                      {extractingRegion && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          color: 'white',
                          padding: '20px',
                          borderRadius: '8px',
                          zIndex: 20
                        }}>
                          <div className="spinner-border spinner-border-sm me-2" role="status" />
                          Extracting text from region...
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Draw existing regions using normalized coordinates - positioned relative to canvas wrapper */}
                  {/* Only show regions for the current page */}
                  {regions.filter(region => (region.page || 1) === currentPage).map((region, index) => {
                    if (!region.normalized && 
                        ((region.x === undefined || region.x === null) || 
                         (region.y === undefined || region.y === null) || 
                         !region.width || !region.height)) return null;
                    
                    const canvasRect = canvasRef.current?.getBoundingClientRect();
                    if (!canvasRect) return null;
                    
                    let displayX, displayY, displayWidth, displayHeight;
                    
                    if (region.normalized) {
                      // Use normalized coordinates (0-1) - zoom/resolution independent!
                      displayX = region.normalized.left * canvasRect.width;
                      displayY = region.normalized.top * canvasRect.height;
                      displayWidth = (region.normalized.right - region.normalized.left) * canvasRect.width;
                      displayHeight = (region.normalized.bottom - region.normalized.top) * canvasRect.height;
                    } else {
                      // Fallback: convert PDF coordinates (for old templates without normalized)
                      const viewport = pdfPage?.getViewport({ scale: 1.0 });
                      const pageHeight = viewport?.height || pdfDimensions?.height || 792;
                      const pageWidth = viewport?.width || pdfDimensions?.width || 612;
                      
                      // Convert PDF points to normalized, then to display pixels
                      const normalizedLeft = region.x / pageWidth;
                      const normalizedTop = 1 - ((region.y + region.height) / pageHeight); // Flip Y
                      const normalizedRight = (region.x + region.width) / pageWidth;
                      const normalizedBottom = 1 - (region.y / pageHeight); // Flip Y
                      
                      displayX = normalizedLeft * canvasRect.width;
                      displayY = normalizedTop * canvasRect.height;
                      displayWidth = (normalizedRight - normalizedLeft) * canvasRect.width;
                      displayHeight = (normalizedBottom - normalizedTop) * canvasRect.height;
                    }
                    
                    // Dim existing regions when drawing a new one
                    const isDrawing = drawingMode;
                    const regionOpacity = isDrawing ? 0.3 : 1;
                    
                    return (
                      <div
                        key={index}
                        style={{
                          position: 'absolute',
                          left: `${displayX}px`,
                          top: `${displayY}px`,
                          width: `${displayWidth}px`,
                          height: `${displayHeight}px`,
                          border: `2px solid rgba(32, 107, 196, ${regionOpacity})`,
                          backgroundColor: `rgba(32, 107, 196, ${0.1 * regionOpacity})`,
                          pointerEvents: 'none',
                          zIndex: 10,
                          opacity: regionOpacity,
                          transition: 'opacity 0.2s ease-in-out'
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          top: '-25px',
                          left: 0,
                          fontSize: '11px',
                          backgroundColor: `rgba(32, 107, 196, ${regionOpacity})`,
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          whiteSpace: 'nowrap',
                          opacity: regionOpacity
                        }}>
                          {region.label}
                        </span>
                      </div>
                    );
                  })}
                  
                  {/* Drawing box - positioned relative to canvas wrapper */}
                  {drawingBox && (drawingBox.width > 0 || drawingBox.height > 0) && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${drawingBox.x}px`,
                        top: `${drawingBox.y}px`,
                        width: `${Math.max(1, drawingBox.width)}px`,
                        height: `${Math.max(1, drawingBox.height)}px`,
                        border: '3px dashed #206bc4',
                        backgroundColor: 'rgba(32, 107, 196, 0.2)',
                        pointerEvents: 'none',
                        zIndex: 20,
                        boxShadow: '0 0 8px rgba(32, 107, 196, 0.5)'
                      }}
                    />
                  )}
                  {/* Debug: Show drawing box state */}
                  {drawingBox && (
                    <div style={{
                      position: 'absolute',
                      top: '-30px',
                      left: 0,
                      fontSize: '10px',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      color: 'white',
                      padding: '2px 4px',
                      zIndex: 100
                    }}>
                      Box: x={drawingBox.x.toFixed(0)}, y={drawingBox.y.toFixed(0)}, w={drawingBox.width.toFixed(0)}, h={drawingBox.height.toFixed(0)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Test Parse Modal */}
      {showTestModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Test Parse Template</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowTestModal(false);
                    setTestFile(null);
                    setTestResults(null);
                  }}
                  disabled={testing}
                ></button>
              </div>
              <div className="modal-body">
                {!pdfFile && !testFile ? (
                  <div className="alert alert-warning">
                    <strong>No PDF available</strong>
                    <p className="mb-0">Please upload a PDF file in the template builder first.</p>
                  </div>
                ) : (
                  <div className="mb-4">
                    <label className="form-label">PDF file to test</label>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge bg-success-lt">
                        Using: {(pdfFile || testFile)?.name || 'Template sample PDF'}
                      </span>
                    </div>
                    <small className="text-muted">
                      Testing against the uploaded PDF file
                    </small>
                  </div>
                )}
                
                {testing && (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Testing...</span>
                    </div>
                    <p className="mt-2 text-muted">Parsing PDF file...</p>
                  </div>
                )}
                
                {testResults && !testing && (
                  <div>
                    {testResults.error ? (
                      <div className="alert alert-danger">
                        <strong>Error:</strong> {testResults.message}
                      </div>
                    ) : (
                      <>
                        <div className="alert alert-success mb-3">
                          <strong>Parse completed successfully</strong>
                        </div>
                        
                        <div className="row mb-3">
                          <div className="col-6">
                            <div className="card">
                              <div className="card-body p-3">
                                <div className="text-muted small">Confidence Score</div>
                                <div className="h3 mb-0">{testResults.confidenceScore}%</div>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="card">
                              <div className="card-body p-3">
                                <div className="text-muted small">Processing Method</div>
                                <div className="h5 mb-0">{testResults.processingMethod}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <h6 className="mb-2">Extracted Fields</h6>
                        <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                          <table className="table table-sm table-bordered">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ width: '40%' }}>Field</th>
                                <th>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(testResults.extractedFields || {})
                                .filter(([key]) => !['fullText', 'fieldLabels', 'templateId', 'templateName', 'templateCode'].includes(key))
                                .map(([key, value]) => (
                                  <tr key={key}>
                                    <td><strong>{key}</strong></td>
                                    <td>
                                      {value === null || value === '' || value === undefined ? (
                                        <span className="text-muted">(empty)</span>
                                      ) : (
                                        <code style={{ wordBreak: 'break-word' }}>{String(value)}</code>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <details className="mt-3">
                          <summary className="cursor-pointer text-muted">View Raw JSON</summary>
                          <pre style={{ 
                            fontSize: '11px', 
                            maxHeight: '200px', 
                            overflow: 'auto', 
                            marginTop: '10px', 
                            backgroundColor: '#f5f5f5',
                            padding: '10px',
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap', 
                            wordWrap: 'break-word' 
                          }}>
                            {JSON.stringify(testResults.extractedFields, null, 2)}
                          </pre>
                        </details>
                      </>
                    )}
                  </div>
                )}
                
                {!testResults && !testing && (
                  <div className="text-center text-muted p-4">
                    <p>Select a PDF file and click "Run Test" to see extraction results</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowTestModal(false);
                    setTestFile(null);
                    setTestResults(null);
                  }}
                  disabled={testing}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleTestParse}
                  disabled={(!pdfFile && !testFile) || testing}
                >
                  {testing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Testing...
                    </>
                  ) : (
                    'Run Test'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default TemplateBuilder;
